import { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { AgGridReact } from 'ag-grid-react';
import type { ColDef } from 'ag-grid-community';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import '../../../assets/css/AGGrid-custom.css';

ModuleRegistry.registerModules([AllCommunityModule]);

export type DealsByMonthStatusBucket = {
  key: string;
  label: string;
  total: number;
  byStatus: Map<string, number>;
};

type RowDef =
  | { kind: 'total'; label: string }
  | { kind: 'status'; label: string; statusName: string };

type GridRow = {
  metric: string;
  isTotal?: boolean;
  values: Record<string, string | number>;
};

export function DealsByMonthStatusTable(props: {
  monthsChrono: DealsByMonthStatusBucket[];
  statusRowNames: string[];
  title?: string;
}) {
  const { statusRowNames, title } = props;
  const monthsChrono = props.monthsChrono ?? [];

  const rows: RowDef[] = [
    { kind: 'total', label: 'Deals' },
    ...statusRowNames.map((s): RowDef => ({ kind: 'status', label: s, statusName: s })),
  ];

  const rowData: GridRow[] = useMemo(() => {
    return rows.map((r) => {
      const values: Record<string, string | number> = {};

      for (const m of monthsChrono) {
        if (r.kind === 'total') {
          values[m.key] = m.total;
        } else {
          const count = m.byStatus.get(r.statusName) ?? 0;
          const pct = m.total > 0 ? (count / m.total) * 100 : 0;
          values[m.key] = `${count} (${Math.round(pct)}%)`;
        }
      }

      return {
        metric: r.label,
        isTotal: r.kind === 'total',
        values,
      };
    });
  }, [monthsChrono, rows]);

  const columnDefs: ColDef<GridRow>[] = useMemo(() => {
    const monthCols: ColDef<GridRow>[] = monthsChrono.map((m) => ({
      headerName: m.label,
      colId: m.key,
      width: 140,
      valueGetter: (p) => p.data?.values?.[m.key] ?? '',
      cellStyle: { textAlign: 'right' as const },
    }));

    return [
      {
        field: 'metric',
        headerName: 'Metric',
        pinned: 'left',
        width: 220,
        cellStyle: (p) => {
          if (p.data?.isTotal) return { fontWeight: 700 as const };
          return { fontWeight: 600 as const };
        },
      },
      ...monthCols,
    ];
  }, [monthsChrono]);

  if (monthsChrono.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        mt: 2,
        backgroundColor: '#fff',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        padding: 2,
      }}
    >
      <Typography className="text-general-section-title" sx={{ mb: 2.5 }}>
        {title ?? 'Monthly Deal Summary'}
      </Typography>

      <Box sx={{ width: '100%' }}>
        <div className="ag-theme-alpine" style={{ width: '100%', overflowX: 'auto' }}>
          <AgGridReact
            rowData={rowData}
            columnDefs={columnDefs}
            defaultColDef={{ resizable: true, filter: false, sortable: false }}
            rowHeight={30}
            headerHeight={30}
            suppressCellFocus={true}
            domLayout="autoHeight"
          />
        </div>
      </Box>
    </Box>
  );
}

